import 'react-native-gesture-handler';
import { NavigationContainer, NavigationContext, NavigationRouteContext, useTheme } from '@react-navigation/native';
import {
  createNativeStackNavigator,
  type NativeStackNavigationOptions,
  type NativeStackScreenProps,
} from '@react-navigation/native-stack';
import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import {
  Dimensions,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import * as SplashScreen from 'expo-splash-screen';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { initialWindowMetrics, SafeAreaProvider } from 'react-native-safe-area-context';
import { AuthProvider, useAuth } from './src/auth/AuthContext';
import { BootSplash } from './src/components/BootSplash';
import { AppDialogHost } from './src/components/AppDialogHost';
import { ToastOutlet } from './src/components/ToastOutlet';
import { LocaleProvider, useLocale } from './src/i18n/LocaleContext';
import { VisualThemeProvider, useVisualTheme } from './src/theme/VisualThemeContext';
import { tr } from './src/i18n/runtime';
import type { RootStackParamList } from './src/navigation/types';
import { navigationRef } from './src/navigation/navigationRef';
import { loadStoredAccess } from './src/api/client';
import { initCrashReporting, reportIssue } from './src/observability/crashReporting';
import { configureErrorReporter } from './src/observability/errorReporter';
import { initAnalytics } from './src/observability/analytics';
import { GlobalRealtimeProvider, type RouteSnapshot } from './src/realtime/GlobalRealtimeProvider';
import { IncomingCallOverlay } from './src/calls/IncomingCallOverlay';
import { MusicPlayerProvider } from './src/music/MusicPlayerContext';
import { ActiveCallLayer } from './src/calls/ActiveCallLayer';
import { AppNativeCallListeners } from './src/notifications/AppNativeCallListeners';
import { NotificationPermissionPrimer } from './src/notifications/NotificationPermissionPrimer';
import { OutboxAutoRetry } from './src/realtime/OutboxAutoRetry';
import { LoginScreen } from './src/screens/LoginScreen';
import { ChatListScreen } from './src/screens/ChatListScreen';
import { ChatPanePlaceholder } from './src/screens/ChatPanePlaceholder';
import {
  CHAT_SIDEBAR_WIDTH,
  PaneSelectionProvider,
  sidebarNavigation,
  usePaneSnapshot,
  useWideLayout,
} from './src/navigation/chatPane';
import { useEscapeToClose } from './src/navigation/escapeToClose';
import type { VisualThemeId } from './src/theme/visualThemeTypes';
import { primeHapticsPref } from './src/util/haptics';
import { primeSoundsPref } from './src/util/appSounds';
import { primeKeepPhotoMetadataPref } from './src/util/photoMetadataPref';
import { handleFromProfileLink } from './src/util/profileLink';
import { channelFromLink } from './src/util/channelLink';
import { requestOpenProfileByHandle } from './src/deeplinks/pendingProfileBridge';
import { requestOpenChannelByLink } from './src/deeplinks/pendingChannelBridge';
import { meetingTokenFromLink } from './src/util/meetingLink';
import { requestJoinMeetingByLink } from './src/deeplinks/pendingMeetingBridge';
import { useIncomingShareIntent } from './src/share/useIncomingShareIntent';

const Stack = createNativeStackNavigator<RootStackParamList>();

/**
 * Входящая ссылка: профиль или канал.
 *
 * <p>Обе формы разбираются в одном месте намеренно. Раньше здесь знали только про {@code /u/},
 * и ссылка на статью, которую раздаёт кнопка «Поделиться», молча проваливалась в обычный
 * запуск — человек видел список чатов вместо статьи. Порядок проверок значения не имеет: пути
 * {@code /u/} и {@code /c/} не пересекаются, и каждый разборщик отвечает null на чужую ссылку.
 */
function openFromLink(url: string | null | undefined): void {
  // Встреча — первой: путь `/m/` не пересекается с `/u/` и `/c/`, а параметр `?m=` не спорит
  // с `?u=`/`?c=`, так что порядок важен лишь тем, что вход в звонок дороже ошибиться.
  const meetingToken = meetingTokenFromLink(url);
  if (meetingToken) {
    requestJoinMeetingByLink(meetingToken);
    return;
  }
  const handle = handleFromProfileLink(url);
  if (handle) {
    requestOpenProfileByHandle(handle);
    return;
  }
  const channel = channelFromLink(url);
  if (channel) {
    requestOpenChannelByLink(channel);
  }
}

// Keep the native launch screen on top of the React canvas until React itself
// has something to paint. Without this hold there is a ~50–400ms gap on iOS
// cold start where the launch image is already gone but Hermes has not yet
// painted, and the user sees a flash of the empty GestureHandlerRootView
// background. Handing over now happens as soon as <BootSplash /> mounts (it
// calls hideAsync itself): BootSplash draws the same plate on the same
// background, only smaller and with a running highlight, so the swap is
// invisible while the session is still being restored.
SplashScreen.preventAutoHideAsync().catch(() => {
  /* second call after hide is fine on iOS; first-launch race is fine too. */
});

/**
 * Watchdog: whatever happens above the React tree, the native splash comes off.
 * Every other hideAsync call sits inside the app tree (BootSplash on mount, the
 * `ready` effect below), so a throw in that subtree used to leave the user in
 * front of a frozen launch image with the error screen hidden underneath it.
 * 15s is deliberately longer than BOOT_STORAGE_TIMEOUT_MS (12s) in AuthContext,
 * so a slow-but-normal boot never trips it; hideAsync is idempotent.
 */
setTimeout(() => {
  void SplashScreen.hideAsync().catch(() => {
    /* already hidden / not configured — harmless. */
  });
}, 15000);

function navigationScreenChrome(themeId: VisualThemeId): NativeStackNavigationOptions {
  if (themeId === 'neutral') {
    return {
      headerStyle: { backgroundColor: '#ffffff' },
      headerTintColor: '#0f172a',
      contentStyle: { backgroundColor: '#e7edf5' },
    };
  }
  if (themeId === 'sun') {
    return {
      headerStyle: { backgroundColor: '#fffbeb' },
      headerTintColor: '#92400e',
      contentStyle: { backgroundColor: '#fde68a' },
    };
  }
  if (themeId === 'dark') {
    return {
      headerStyle: { backgroundColor: '#18181b' },
      headerTintColor: '#fafafa',
      contentStyle: { backgroundColor: '#09090b' },
    };
  }
  if (themeId === 'cosmos') {
    return {
      headerStyle: { backgroundColor: '#0f172a' },
      headerTintColor: '#a5b4fc',
      contentStyle: { backgroundColor: '#020617' },
    };
  }
  // varangian (default)
  return {
    headerStyle: { backgroundColor: '#1e293b' },
    headerTintColor: '#f8fafc',
    contentStyle: { backgroundColor: '#0c1924' },
  };
}

// All routes on the *initial* authed path (ChatList) and all global providers
// load synchronously: the splash is held until ready, so there is no Suspense
// boundary the user would see flash. ChatScreen / CallScreen stay lazy because
// they are only reached via navigation, not at app launch.
const ChatScreen = React.lazy(() =>
  import('./src/screens/ChatScreen').then((m) => ({ default: m.ChatScreen })),
);
const CallScreen = React.lazy(() =>
  import('./src/screens/CallScreen').then((m) => ({ default: m.CallScreen })),
);
const CreateChannelPostScreen = React.lazy(() =>
  import('./src/screens/CreateChannelPostScreen').then((m) => ({ default: m.CreateChannelPostScreen })),
);
const ChannelPostScreen = React.lazy(() =>
  import('./src/screens/ChannelPostScreen').then((m) => ({ default: m.ChannelPostScreen })),
);
const ChannelPostCommentsModal = React.lazy(() =>
  import('./src/screens/ChannelPostCommentsModal').then((m) => ({ default: m.ChannelPostCommentsModal })),
);
// «Новое сообщение», новая группа и новый канал (вариант A аудита 2026-10-04) — тоже лениво:
// до них доходят только через «+», а не при запуске.
const NewMessageScreen = React.lazy(() =>
  import('./src/screens/NewMessageScreen').then((m) => ({ default: m.NewMessageScreen })),
);
const NewGroupScreen = React.lazy(() =>
  import('./src/screens/NewGroupScreen').then((m) => ({ default: m.NewGroupScreen })),
);
const NewChannelScreen = React.lazy(() =>
  import('./src/screens/NewChannelScreen').then((m) => ({ default: m.NewChannelScreen })),
);
// Встречи по ссылке — только у организатора и только через кнопку в шапке.
const MeetingsScreen = React.lazy(() =>
  import('./src/screens/MeetingsScreen').then((m) => ({ default: m.MeetingsScreen })),
);

/**
 * Last-resort UI for unrecoverable JS errors. Without this a top-level
 * React-tree error would render an empty (white on iOS, dark on Android)
 * screen with no on-device way to extract the stack. This boundary catches
 * synchronous render/effect errors at any depth and prints them so the user
 * can screenshot the message.
 *
 * NOTE: We intentionally do NOT touch `ErrorUtils.setGlobalHandler` here.
 * On Hermes/iOS that hook is non-standard and any subtle signature
 * mismatch can prevent the JS bundle from finishing its boot phase,
 * leaving the user staring at the native white launch view forever
 * while the rest of the app (PushKit, FCM, CallKit) keeps working.
 */
class GlobalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // Первым делом снимаем нативный сплэш: этот boundary стоит ВЫШЕ AppContent, так что
    // после падения до `ready` никто больше hideAsync не вызовет — экран с ошибкой остался
    // бы навсегда спрятан под неподвижной заставкой.
    void SplashScreen.hideAsync().catch(() => {
      /* already hidden / not configured — harmless. */
    });
    console.error('[GlobalErrorBoundary]', error, info.componentStack);
    // `fatal`, а не `error`: сюда попадает только то, после чего человек видит экран с текстом
    // ошибки вместо приложения. Это происшествие, и оно обязано поднять оповещение (OBS-2).
    reportIssue('app', error, {
      level: 'fatal',
      context: {
        boundary: 'GlobalErrorBoundary',
        componentStack:
          typeof info.componentStack === 'string' ? info.componentStack.slice(0, 500) : undefined,
      },
    });
  }

  render(): React.ReactNode {
    const err = this.state.error;
    if (!err) {
      return this.props.children;
    }
    return (
      <View style={styles.errorRoot}>
        <Text style={styles.errorTitle}>SVYAZ crashed at startup</Text>
        <ScrollView style={styles.errorScroll} contentContainerStyle={styles.errorScrollContent}>
          <Text style={styles.errorMessage}>{err.name}: {err.message}</Text>
          {typeof err.stack === 'string' ? (
            <Text style={styles.errorStack}>{err.stack}</Text>
          ) : null}
        </ScrollView>
      </View>
    );
  }
}

function GuestNavigator({ screenOptions }: { screenOptions: NativeStackNavigationOptions }) {
  return (
    <Stack.Navigator key="guest" initialRouteName="Login" screenOptions={screenOptions}>
      <Stack.Screen name="Login" component={LoginScreen} options={{ headerShown: false }} />
    </Stack.Navigator>
  );
}

/**
 * Маршрут списка чатов. На широком окне сам список живёт в левой панели (ChatListSidebar),
 * а здесь, справа, — заглушка «Выберите чат»: «назад» из чата приводит к ней.
 */
function ChatListRoute(props: NativeStackScreenProps<RootStackParamList, 'ChatList'>) {
  const wide = useWideLayout();
  return wide ? <ChatPanePlaceholder /> : <ChatListScreen {...props} />;
}

/**
 * Левая панель двухпанельного режима (navigation/chatPane): тот же экран списка, но вне
 * стека — с переходником навигации, который открывает всё в правой панели.
 */
function ChatListSidebar() {
  const snap = usePaneSnapshot();
  const { colors } = useTheme();
  const selection = useMemo(
    () => ({ chatId: snap.chatId, savedOpen: snap.savedOpen }),
    [snap.chatId, snap.savedOpen],
  );
  const route = useMemo(
    () => ({ key: 'ChatList-sidebar', name: 'ChatList' as const, params: snap.listParams }),
    [snap.listParams],
  );
  return (
    <View style={[styles.paneSidebar, { borderRightColor: colors.border }]}>
      <NavigationContext.Provider
        value={sidebarNavigation as unknown as React.ContextType<typeof NavigationContext>}>
        <NavigationRouteContext.Provider value={route}>
          <PaneSelectionProvider value={selection}>
            <ChatListScreen navigation={sidebarNavigation} route={route} />
          </PaneSelectionProvider>
        </NavigationRouteContext.Provider>
      </NavigationContext.Provider>
    </View>
  );
}

function AuthedNavigator({ screenOptions }: { screenOptions: NativeStackNavigationOptions }) {
  const wide = useWideLayout();
  useEscapeToClose();
  // Обёртка одна и та же в обоих режимах, и стек всегда второй ребёнок: смена ширины окна
  // добавляет или убирает панель слева, но не пересоздаёт стек с открытым чатом.
  return (
    <View style={styles.paneRow}>
      {wide ? <ChatListSidebar /> : null}
      <View style={styles.paneMain}>
        <AuthedStack screenOptions={screenOptions} />
      </View>
    </View>
  );
}

function AuthedStack({ screenOptions }: { screenOptions: NativeStackNavigationOptions }) {
  // Заголовки — через контекст, а не `tr`: `tr` читает язык в момент отрисовки, а навигатор
  // рисуется раньше, чем приходит язык аккаунта, и без подписки так и оставался со старым
  // («Новое сообщение» в английском интерфейсе на десктопе).
  const { t } = useLocale();
  return (
    <Stack.Navigator key="authed" initialRouteName="ChatList" screenOptions={screenOptions}>
      <Stack.Screen name="ChatList" component={ChatListRoute} options={{ headerShown: false }} />
      <Stack.Screen
        name="Chat"
        component={ChatScreen}
        options={({ route }) => ({ title: route.params.title })}
      />
      <Stack.Screen name="Call" component={CallScreen} options={{ headerShown: false }} />
      <Stack.Screen name="NewMessage" component={NewMessageScreen} options={{ title: t('newMessageTitle') }} />
      <Stack.Screen name="NewGroup" component={NewGroupScreen} options={{ title: t('newMessageNewGroup') }} />
      <Stack.Screen name="NewChannel" component={NewChannelScreen} options={{ title: t('newMessageNewChannel') }} />
      <Stack.Screen name="Meetings" component={MeetingsScreen} options={{ title: t('meetingsTitle') }} />
      <Stack.Screen
        name="CreateChannelPost"
        component={CreateChannelPostScreen}
        options={({ route }) => ({
          // Один экран на «написать» и «исправить» — заголовок обязан их различать, иначе
          // правка старой статьи выглядит как начало новой.
          title: route.params.postId ? t('channelPostEditTitle') : t('navTitleNewPost'),
        })}
      />
      <Stack.Screen
        name="ChannelPost"
        component={ChannelPostScreen}
        options={{ title: t('navTitlePost') }}
      />
      <Stack.Screen
        name="ChannelPostComments"
        component={ChannelPostCommentsModal}
        options={{
          title: t('channelPostComments'),
          headerShown: false,
          presentation: 'modal',
          animation: 'slide_from_bottom',
        }}
      />
    </Stack.Navigator>
  );
}

function AppContent() {
  const { ready, accessToken } = useAuth();
  const { navigationTheme, resolvedVisualTheme } = useVisualTheme();
  const routeRef = useRef<RouteSnapshot>({ name: 'Login', chatId: undefined });
  const stackChrome = useMemo(() => navigationScreenChrome(resolvedVisualTheme), [resolvedVisualTheme]);

  // Тактильная отдача (стори 3.1): настройка хранится асинхронно, а отклик нужен синхронно в
  // момент нажатия. Поднимаем её в память один раз при старте; до этого действует умолчание.
  useEffect(() => {
    void primeHapticsPref();
    // Стори 18.1: то же, что с тактильной отдачей — иначе переключатель EXIF при открытии
    // настроек мигает «выключено» и только потом встаёт в своё положение.
    void primeKeepPhotoMetadataPref();
    // И то же самое со звуками: решение «играть или молчать» принимается синхронно в
    // обработчике WS-сообщения, ждать там чтения с диска нечем.
    void primeSoundsPref();
  }, []);

  /**
   * Содержимое из системного меню «Поделиться» (стори 8.7). Здесь, в корне: приложение может
   * подниматься именно из этого меню, и приёмник обязан стоять раньше любого экрана. Само
   * содержимое ждёт в буфере, пока список чатов не покажет выбор адресата.
   */
  useIncomingShareIntent();

  // Ссылка на публичный профиль (стори 18.3). Слушаем оба пути, и оба обязательны: приложение
  // уже запущено (событие `url`) и приложение поднимается по этой самой ссылке
  // (`getInitialURL`) — второе как раз про гостя, который только что поставил приложение.
  // Ник кладём в буфер: открывать диалог тут некому, экран чатов ещё не смонтирован, а у
  // человека без аккаунта впереди ещё и вход.
  useEffect(() => {
    let cancelled = false;
    void Linking.getInitialURL()
      .then((url) => {
        if (cancelled) return;
        openFromLink(url);
      })
      .catch(() => {
        /* Ссылки нет или платформа её не отдала — обычный запуск. */
      });
    const sub = Linking.addEventListener('url', ({ url }) => {
      openFromLink(url);
    });
    return () => {
      cancelled = true;
      sub.remove();
    };
  }, []);

  // Idempotent repeat of the hideAsync that <BootSplash /> already did on mount
  // (`ready` starts as false, so BootSplash always mounts at least once). Kept
  // because hideAsync can lose the race with the native view on a cold start,
  // and calling it again once we actually have content is free. On web the
  // whole module is a noop.
  useEffect(() => {
    if (!ready) return;
    void SplashScreen.hideAsync().catch(() => {
      /* already hidden / not configured — harmless. */
    });
  }, [ready]);

  if (!ready) {
    // The Keychain read / refresh is still in flight. Show our own splash: same
    // background colour as the native one, same plate, plus a blue highlight
    // running along its outline so the wait does not look like a freeze. The
    // next render lands the user directly on ChatList or Login.
    return <BootSplash />;
  }

  return (
    <NavigationContainer
      ref={navigationRef}
      theme={navigationTheme}
      onStateChange={() => {
        if (Platform.OS === 'web' && typeof document !== 'undefined') {
          const ae = document.activeElement;
          if (ae instanceof HTMLElement) {
            ae.blur();
          }
        }
        const r = navigationRef.getCurrentRoute();
        const name = r?.name ?? '';
        let chatId: string | undefined;
        const p = r?.params as { chatId?: string } | undefined;
        if ((name === 'Chat' || name === 'Call' || name === 'ChannelPost') && p?.chatId) {
          chatId = p.chatId;
        }
        routeRef.current = { name, chatId };
      }}>
      {accessToken ? <AuthedRuntime routeRef={routeRef} stackChrome={stackChrome} /> : <GuestNavigator screenOptions={stackChrome} />}
      {/**
       * Must be mounted as early as possible (including guest state) so CallKeep
       * can drain queued lock-screen answer/end actions after a cold start on iOS.
       */}
      <AppNativeCallListeners />
      {/**
       * Диалоги приложения в цветах темы (заказ владельца 2026-08-31). Здесь, а не на
       * экранах: спросить «вы уверены?» нужно из десятка обработчиков, а нарисовать —
       * одной карточкой поверх всего. Внутри NavigationContainer, потому что тема и язык
       * приходят из провайдеров снаружи, а сам хост ничего не рисует, пока не спросят.
       */}
      <AppDialogHost />
      {/** Уведомления «Скопировано», «Отменить» — поверх экранов; в листах свой вывод (toastBridge). */}
      <ToastOutlet />
    </NavigationContainer>
  );
}

async function navigateFromPushData(d: Record<string, unknown>): Promise<void> {
  if (String(d.type) === 'contact_request') {
    // Пуш заявки ведёт прямо к заявкам на экране «Новое сообщение». Через мост, а не сразу
    // навигацией: на холодном старте навигация ещё не готова, и мост дождётся списка чатов.
    const { requestOpenDruzhina } = await import('./src/notifications/openDruzhinaBridge');
    requestOpenDruzhina();
    return;
  }
  const pushType = String(d.type);
  if (pushType === 'birthday' || pushType === 'scheduled_failed') {
    // Оба ведут в список чатов: там и карточка «сегодня день рождения» с кнопкой
    // «Поздравить», и «Избранное» с разделом отложенных, откуда видно, что не ушло.
    if (navigationRef.isReady()) navigationRef.navigate('ChatList');
    return;
  }
  if (pushType === 'comment_reply') {
    // Ведём прямо в ветку комментариев поста: открыть канал и предложить человеку самому
    // найти, на какой из своих комментариев ему ответили, — это не уведомление, а загадка.
    // Без postId (пуш от старого бэкенда) остаётся канал — это всё, что известно.
    if (!navigationRef.isReady()) return;
    const channelTitle = typeof d.channelTitle === 'string' ? d.channelTitle : tr('chatFallback');
    if (typeof d.chatId === 'string' && typeof d.postId === 'string' && d.postId.length > 0) {
      navigationRef.navigate('ChannelPostComments', {
        postId: d.postId,
        chatId: d.chatId,
        channelTitle,
      });
    } else if (typeof d.chatId === 'string' && d.chatId.length > 0) {
      navigationRef.navigate('Chat', { chatId: d.chatId, title: channelTitle });
    } else {
      navigationRef.navigate('ChatList');
    }
    return;
  }
  if (pushType === 'reminder') {
    // «Напомнить об этом сообщении» ведёт в исходный чат; напоминание без привязки — в
    // список. Заголовок из пуша сюда НЕ подставляем: он служебный («⏰ Напоминание»), и в
    // шапке чата вместо имени собеседника читался бы как поломка.
    if (!navigationRef.isReady()) return;
    if (typeof d.chatId === 'string' && d.chatId.length > 0) {
      navigationRef.navigate('Chat', { chatId: d.chatId, title: tr('chatFallback') });
    } else {
      navigationRef.navigate('ChatList');
    }
    return;
  }
  if (typeof d.chatId !== 'string') return;
  const chatId = d.chatId;
  const title = typeof d.title === 'string' ? d.title : tr('chatFallback');
  // Новая статья канала: пуш несёт postId, и тап обязан открыть саму статью. Раньше этого поля
  // в нём не было, тап приводил в ленту канала, и статью надо было искать глазами и нажимать
  // второй раз — при том что пуш об ответе на комментарий вёл прямо в ветку.
  if (String(d.type) === 'message' && typeof d.postId === 'string' && d.postId.length > 0) {
    if (!navigationRef.isReady()) return;
    navigationRef.navigate('ChannelPost', {
      postId: d.postId,
      chatId,
      channelTitle: title,
    });
    return;
  }
  const callerName = typeof d.callerName === 'string' ? d.callerName : title;
  const callMode = d.callMode === 'video' ? 'video' : 'audio';
  const callScope = d.callScope === 'group' ? 'group' : 'direct';
  if (String(d.type) === 'call') {
    const { shouldSuppressIncomingCallPush } = await import('./src/notifications/pushDedupe');
    if (shouldSuppressIncomingCallPush(chatId)) return;
    // iOS показывает входящий через CallKit (APNs VoIP) — навигируем в чат.
    // web и Android — in-app оверлей. Оверлей не зависит от готовности навигации:
    // Modal рендерится сам, а мост буферизует payload до монтирования оверлея
    // (важно для холодного старта из полноэкранного уведомления на Android).
    if (Platform.OS !== 'ios') {
      const [{ presentIncomingCallUI, callerAvatarFromPushData }, { groupTitleFromPushData }] =
        await Promise.all([
          import('./src/calls/incomingCallUiBridge'),
          import('./src/calls/callLabels'),
        ]);
      presentIncomingCallUI({
        chatId,
        callerName,
        callMode,
        callScope,
        ...callerAvatarFromPushData(d),
        ...groupTitleFromPushData(d),
      });
      return;
    }
    if (navigationRef.isReady()) navigationRef.navigate('Chat', { chatId, title });
    return;
  }
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('Chat', { chatId, title });
}

function useNativePushOpenHandlers() {
  useEffect(() => {
    let alive = true;
    let unsubOpen: (() => void) | undefined;
    let unsubNotifee: (() => void) | undefined;

    void (async () => {
      try {
        const [{ default: messaging }, { default: notifee, EventType }, { ensureNotifyPermission }] =
          await Promise.all([
            import('@react-native-firebase/messaging'),
            import('@notifee/react-native'),
            import('./src/realtime/notifyIncomingMessage'),
          ]);
        await ensureNotifyPermission();
        if (!alive) return;

        unsubOpen = messaging().onNotificationOpenedApp((remoteMessage) => {
          const d = remoteMessage.data as Record<string, unknown> | undefined;
          if (!d) return;
          void navigateFromPushData(d);
        });

        unsubNotifee = notifee.onForegroundEvent(({ type, detail }) => {
          if (type !== EventType.PRESS || !detail.notification) return;
          const raw = detail.notification.data ?? {};
          const d: Record<string, unknown> = { ...raw };
          void navigateFromPushData(d);
        });

        void messaging()
          .getInitialNotification()
          .then((remoteMessage) => {
            const d = remoteMessage?.data as Record<string, unknown> | undefined;
            if (!d) return;
            void navigateFromPushData(d);
          })
          .catch(() => {});

        // Полноэкранное уведомление о входящем звонке (Android) рисуется через notifee,
        // поэтому его нажатие из УБИТОГО состояния не приходит в messaging().getInitialNotification()
        // — только сюда. Без этого тап по звонку на заблокированном/убитом приложении не
        // показывал оверлей.
        void notifee
          .getInitialNotification()
          .then((initial) => {
            const d = initial?.notification?.data as Record<string, unknown> | undefined;
            if (!d) return;
            void navigateFromPushData(d);
          })
          .catch(() => {});
      } catch {
        /* Native push modules are optional during cold start. */
      }
    })();

    return () => {
      alive = false;
      unsubOpen?.();
      unsubNotifee?.();
    };
  }, []);
}

function AuthedRuntime({
  routeRef,
  stackChrome,
}: {
  routeRef: React.MutableRefObject<RouteSnapshot>;
  stackChrome: NativeStackNavigationOptions;
}) {
  useNativePushOpenHandlers();
  const { user } = useAuth();
  const musicOwner = user?.userId ?? null;

  return (
    <GlobalRealtimeProvider routeRef={routeRef}>
      {/* Разрешение на уведомления — по нажатию, с пояснением; см. сам компонент. */}
      <NotificationPermissionPrimer />
      {/* Неотправленное повторяется при возврате связи — из любого экрана (UX-48). */}
      <OutboxAutoRetry />
      <IncomingCallOverlay />
      {/**
       * ChatScreen / CallScreen are still `React.lazy` (only loaded on
       * navigation, not at boot), so we need a Suspense boundary. The fallback
       * is `null` on purpose — react-native-screens keeps the previous screen
       * on top until the new one is mounted, so the user sees the chat list
       * during the millisecond Hermes spends evaluating the chunk, not a
       * flashing boot indicator.
       */}
      {/**
       * Звонок живёт вне экрана звонка: слой держит сессию и плашку «Вернуться к звонку» над
       * навигатором — так звонок можно свернуть (аудит удобства 2026-10-04, P0-5).
       */}
      {/**
       * Музыкальный плеер (2026-10-04) — над навигатором по той же причине, что и звонок: музыка
       * не принадлежит экрану и играет, пока человек переписывается или телефон в кармане.
       */}
      {/* key — по аккаунту: у каждого своя фонотека, и чужая не должна мелькнуть даже на кадр. */}
      <MusicPlayerProvider key={musicOwner ?? 'none'} userId={musicOwner}>
        <ActiveCallLayer>
          <Suspense fallback={null}>
            <AuthedNavigator screenOptions={stackChrome} />
          </Suspense>
        </ActiveCallLayer>
      </MusicPlayerProvider>
    </GlobalRealtimeProvider>
  );
}

export default function App() {
  useEffect(() => {
    // Enable Crashlytics collection (release only). Native crashes are captured
    // automatically once the module is linked; JS errors flow via the boundary.
    //
    // OBS-2. Токен передаётся сюда функцией, а не импортом внутри модуля отчётов: тот
    // импортируется из `api/client`, и прямая ссылка обратно замкнула бы цикл.
    configureErrorReporter(loadStoredAccess);
    initCrashReporting();
    // Продуктовые события (FN-1.2). Собирать или молчать, решает сам модуль: в отладочных сборках
    // он выключен, иначе разработка подмешивалась бы к DAU.
    initAnalytics();
  }, []);
  return (
    <GlobalErrorBoundary>
      <GestureHandlerRootView style={styles.root}>
        {/**
         * `initialMetrics` lets safe-area-context return synchronous insets on
         * the very first render. Without it, after an iOS force-quit relaunch
         * the provider sometimes never receives the native measurement event
         * (StatusBar/SafeAreaManager not yet wired up), so any descendant that
         * reads `useSafeAreaInsets()` during mount blocks first paint and the
         * user is stuck looking at the GestureHandlerRootView background.
         */}
        <SafeAreaProvider initialMetrics={initialWindowMetrics}>
          <AuthProvider>
            <LocaleProvider>
              <VisualThemeProvider>
                <AppContent />
              </VisualThemeProvider>
            </LocaleProvider>
          </AuthProvider>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </GlobalErrorBoundary>
  );
}

const styles = StyleSheet.create({
  paneRow: { flex: 1, flexDirection: 'row' },
  paneMain: { flex: 1, minWidth: 0 },
  paneSidebar: {
    width: CHAT_SIDEBAR_WIDTH,
    // Обои списка рассчитаны на всё окно и выходили бы под правую панель.
    overflow: 'hidden',
    borderRightWidth: StyleSheet.hairlineWidth,
  },
  root: {
    flex: 1,
    minHeight: Dimensions.get('window').height,
    width: '100%',
    overflow: 'hidden',
    // Match the chat-list / login background so that on the unlikely chance
    // the native splash is dismissed before NavigationContainer paints (e.g.
    // user reopens the app from a stale CallKit answer), the user sees the
    // theme color instead of UIWindow white.
    backgroundColor: '#e7edf5',
  },
  errorRoot: {
    flex: 1,
    paddingTop: 56,
    paddingHorizontal: 16,
    paddingBottom: 16,
    backgroundColor: '#0f172a',
  },
  errorTitle: {
    color: '#f87171',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
  },
  errorScroll: { flex: 1 },
  errorScrollContent: { paddingBottom: 32 },
  errorMessage: {
    color: '#fca5a5',
    fontSize: 14,
    marginBottom: 12,
  },
  errorStack: {
    color: '#fda4af',
    fontSize: 12,
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
  },
});
